import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS } from '~/shared/constants';

type CourseCategoryType = {
  _id: string;
  title?: string | null;
  alias?: string | null;
  categoryIndex?: number | null;
};

type PaginationPageInfoType = {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  currentPage: number;
  perPage: number;
  itemCount: number;
  pageCount: number;
};

type PaginationStateType<TItem> = {
  items: TItem[];
  pageInfo: PaginationPageInfoType;
};

type FilesPaginationStateType<TItem> = PaginationStateType<TItem> & {
  isLoading: boolean;
};

type CourseCategoriesSliceType = {
  selectedPersonalExerciseCourseCategoryId: string | null;
  selectedSharedExerciseCourseCategoryId: string | null;
  courseCategories: CourseCategoryType[];
  trialLessonCourseCategories: CourseCategoryType[];
  selectedToDoExerciseCourseCategoryId: string | null;
  changedExerciseCourseCategoryId: string | null;
  selectedCourseCategory: CourseCategoryType | null;
  courseCategoryIndex: string | null;
  editableCourseCategory: CourseCategoryType | null;
  courseCategoriesPagination: PaginationStateType<CourseCategoryType>;
  personalFilesCourseCategoriesPagination: FilesPaginationStateType<CourseCategoryType>;
  sharedFilesCourseCategoriesPagination: FilesPaginationStateType<CourseCategoryType>;
};

const createPageInfo = (): PaginationPageInfoType => ({
  hasPreviousPage: false,
  hasNextPage: false,
  currentPage: 1,
  perPage: LIMIT_FOR_FETCHING_ADMIN_ITEMS,
  itemCount: 0,
  pageCount: 1,
});

const createPaginationState = <TItem>(): PaginationStateType<TItem> => ({
  items: [],
  pageInfo: createPageInfo(),
});

const createFilesPaginationState = <TItem>(): FilesPaginationStateType<TItem> => ({
  items: [],
  isLoading: false,
  pageInfo: createPageInfo(),
});

export const pagesInitialState = createFilesPaginationState<CourseCategoryType>();

const initialState: CourseCategoriesSliceType = {
  selectedPersonalExerciseCourseCategoryId: null,
  selectedSharedExerciseCourseCategoryId: null,
  courseCategories: [],
  trialLessonCourseCategories: [],
  selectedToDoExerciseCourseCategoryId: null,
  courseCategoryIndex: null,
  changedExerciseCourseCategoryId: null,
  selectedCourseCategory: null,
  editableCourseCategory: null,
  courseCategoriesPagination: createPaginationState<CourseCategoryType>(),
  personalFilesCourseCategoriesPagination: createFilesPaginationState<CourseCategoryType>(),
  sharedFilesCourseCategoriesPagination: createFilesPaginationState<CourseCategoryType>(),
};

export const courseCategoriesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    setSelectedToDoExerciseCourseCategoryId: (state, action: PayloadAction<string | null>) => {
      state.selectedToDoExerciseCourseCategoryId = action.payload;
    },

    setSelectedSharedExerciseCourseCategoryId: (state, action: PayloadAction<string | null>) => {
      state.selectedSharedExerciseCourseCategoryId = action.payload;
    },

    setChangedExerciseCourseCategoryId: (state, action: PayloadAction<string | null>) => {
      state.changedExerciseCourseCategoryId = action.payload;
    },

    setSelectedPersonalExerciseCourseCategoryId: (state, action: PayloadAction<string | null>) => {
      state.selectedPersonalExerciseCourseCategoryId = action.payload;
    },

    setTrialLessonCourseCategories: (state, action: PayloadAction<CourseCategoryType[]>) => {
      state.trialLessonCourseCategories = action.payload;
    },

    setEditableCourseCategory: (state, action: PayloadAction<CourseCategoryType | null>) => {
      state.editableCourseCategory = action.payload;
    },

    setSelectedCourseCategory: (state, action: PayloadAction<CourseCategoryType | null>) => {
      state.selectedCourseCategory = action.payload;
    },

    setCourseCategoriesPagination: (state, action: PayloadAction<PaginationStateType<CourseCategoryType>>) => {
      state.courseCategoriesPagination = action.payload;
    },

    setCourseCategoryIndex: (state, action: PayloadAction<string | null>) => {
      state.courseCategoryIndex = action.payload;
    },

    setPersonalFilesCourseCategoriesPagination: (
      state,
      action: PayloadAction<FilesPaginationStateType<CourseCategoryType>>,
    ) => {
      state.personalFilesCourseCategoriesPagination = action.payload;
    },

    appendPersonalFilesCourseCategoriesPagination: (
      state,
      action: PayloadAction<PaginationStateType<CourseCategoryType>>,
    ) => {
      state.personalFilesCourseCategoriesPagination.items.push(...action.payload.items);
      state.personalFilesCourseCategoriesPagination.pageInfo = action.payload.pageInfo;
    },

    setPersonalFilesCourseCategoriesPaginationLoading: (state, action: PayloadAction<boolean>) => {
      state.personalFilesCourseCategoriesPagination.isLoading = action.payload;
    },

    setCourseCategories: (state, action: PayloadAction<CourseCategoryType[]>) => {
      state.courseCategories = action.payload;
    },

    setSharedFilesCourseCategoriesPagination: (
      state,
      action: PayloadAction<FilesPaginationStateType<CourseCategoryType>>,
    ) => {
      state.sharedFilesCourseCategoriesPagination = action.payload;
    },

    appendSharedFilesCourseCategoriesPagination: (
      state,
      action: PayloadAction<PaginationStateType<CourseCategoryType>>,
    ) => {
      state.sharedFilesCourseCategoriesPagination.items.push(...action.payload.items);
      state.sharedFilesCourseCategoriesPagination.pageInfo = action.payload.pageInfo;
    },

    setSharedFilesCourseCategoriesPaginationLoading: (state, action: PayloadAction<boolean>) => {
      state.sharedFilesCourseCategoriesPagination.isLoading = action.payload;
    },
  },
});

export const {
  setSelectedToDoExerciseCourseCategoryId,
  setCourseCategories,
  setTrialLessonCourseCategories,
  appendPersonalFilesCourseCategoriesPagination,
  setPersonalFilesCourseCategoriesPaginationLoading,
  setPersonalFilesCourseCategoriesPagination,
  appendSharedFilesCourseCategoriesPagination,
  setSharedFilesCourseCategoriesPaginationLoading,
  setEditableCourseCategory,
  setSharedFilesCourseCategoriesPagination,
  setCourseCategoryIndex,
  setChangedExerciseCourseCategoryId,
  setSelectedCourseCategory,
  setSelectedSharedExerciseCourseCategoryId,
  setSelectedPersonalExerciseCourseCategoryId,
  setCourseCategoriesPagination,
} = courseCategoriesSlice.actions;

export default courseCategoriesSlice.reducer;
